// @flow
import React, { type Element } from 'react';
import { compose, pure } from 'recompose';
import { Alert, Grid, Col, Row, ProgressBar } from 'angieslist-react-components';
import { stylesColors } from 'angieslist-styles';
import { analytics, analyticsActions, urlCatalog } from 'angieslist-app-platform';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { connect } from 'react-redux';
import { get, find, sum, isEmpty, chunk } from 'lodash';
import { itemClicked, trackEvent } from '../../../../app/browser/analyticsHelpers';
import AddedBusinessHandler from './AddedBusinessHandler';
import SingleCard from './DashboardV2/Card/SingleCard';
import NewActivityCard from './DashboardV2/NewActivityCard';
import ClientSuccessManagerCard from './DashboardV2/ClientSuccessManagerCard';
import ProfileCompletionCard from './DashboardV2/ProfileCompletionCard';
import DealsModal from './DashboardV2/DealsModal';
import serviceProviderActions from '../../../../app/common/serviceProvider/serviceProviderActions';
import performanceActions from '../../common/performanceActions';
import type { ServiceProvider } from '../../../../app/types';
import type { PerformanceReducerState } from '../../common/performanceReducer';
import * as advertiseActions from '../../../../app/common/advertise/advertiseActions';
import * as homeActions from '../../common/homeActions';
import inboxActions from '../../../inbox/common/inboxActions';
import CustomerContactsChart from './CustomerContactsChart';
import { GenericCard } from './DashboardV2/Card/GenericCard';
import { BusinessHighlightsCard } from './DashboardV2/Card/BusinessHighlightsCard';
import type { BusinessListReducerState } from '../../common/businessListReducer';
import type { ProfileReducerState } from '../../../profile/common/profileReducer';
import profileActions from '../../../profile/common/profileActions';
import './ServiceProviderDashboard.scss';
import categoryGroupActions from '../../../../app/common/category-group/categoryGroupActions';
import AutomationsOnboardingModal from '../../../../app/browser/AutomationsOnboardingModal';

type Props = {
  serviceProvider: ServiceProvider,
  serviceProviderProfile: ProfileReducerState,
  performance: PerformanceReducerState,
  getServiceProviderMetrics: typeof serviceProviderActions.getServiceProviderMetrics,
  getServiceProviderAccountOwner: typeof serviceProviderActions.getServiceProviderAccountOwner,
  getContactMetricsByServiceProvider: typeof performanceActions.getContactMetricsByServiceProvider,
  getCustomerAttributesByUuid: typeof performanceActions.getCustomerAttributesByUuid,
  experiment: any,
  home: any,
  getAllHighlights: typeof homeActions.getAllHighlights,
  updateBusinessHighlights: typeof homeActions.updateBusinessHighlights,
  businessList: BusinessListReducerState,
  updateServiceProviderId: typeof serviceProviderActions.updateServiceProviderId,
  getServiceProviderProfile: typeof profileActions.getServiceProviderProfile,
  inbox: Object,
  getAutomationMessage: typeof inboxActions.getAutomationMessage,
  getCategoryGroup: typeof categoryGroupActions.getCategoryGroup;
};

type State = {
  isAutomationsOnboardingModalOpen: boolean,
};

const calculateContactsPercentage = (contactCount, contactTotal) =>
  ((contactTotal > 0) ? (contactCount / contactTotal) * 100 : 0);

export const getCookieByName: ((string) => string) = (cookieName) => {
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  let cookieValue = 'false';

  cookieArray.forEach((cookie) => {
    const cookieTrim = cookie.trim();
    if (cookieTrim.indexOf(cookieName.concat('=')) === 0) {
      cookieValue = cookieTrim.substring(cookieName.length + 1);
    }
  });
  return cookieValue;
};

export const isNSDashViewedCookie = 'isNSDashViewed';
export const isStep4Complete = 'step4complete';
export const setExpirationDate: ((number) => string) = (days) => {
  const expiration = days * 24 * 60 * 60 * 1000;
  return new Date(Date.now() + expiration).toUTCString();
};

let categoryGroupFetched;

export class ServiceProviderDashboardComponent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isAutomationsOnboardingModalOpen: false,
    };
    categoryGroupFetched = false;
  }

  componentDidMount() {
    const {
      serviceProvider: {
        id: serviceProviderId,
      },
    } = this.props;

    const endTime = new Date();
    const startTime = new Date(new Date().setDate(endTime.getDate() - 90));
    this.props.updateServiceProviderId(serviceProviderId.toString());
    this.props.getServiceProviderMetrics(serviceProviderId);
    this.props.getServiceProviderAccountOwner(serviceProviderId);
    this.props.getContactMetricsByServiceProvider({ serviceProviderId, startTime, endTime });
    this.props.getAllHighlights();
    const { uuid } = this.props.serviceProvider;
    this.props.getServiceProviderProfile({ uuid });
    this.props.getCustomerAttributesByUuid({ serviceProviderId, uuid });
    window.addEventListener('resize', this.resize);
    if (window.analytics !== undefined) {
      window.analytics.identify({
        NorthstarStatus: get(this.props.serviceProvider, 'isNorthStar', false),
        advertisingStatus: get(this.props.serviceProvider, 'isAdvertiser'),
        onlineOfferType: get(this.props.serviceProvider, 'onlineUpgradeOffer', ''),
        advertiserStartDate: get(this.props.serviceProvider, 'advertiserStartDate', ''),
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { currentProfile }: {currentProfile: any} = this.props.serviceProviderProfile;
    const { getAutomationMessage, inbox, serviceProvider } = this.props;

    if (
      (currentProfile !== prevProps.serviceProviderProfile.currentProfile) ||
      (currentProfile.response != null && !categoryGroupFetched)
    ) {
      categoryGroupFetched = true;
      if (currentProfile.response) {
        this.props.getCategoryGroup(currentProfile.response.categories[0].id);
      }
    }

    if (prevProps.inbox.isAutomationEnabled !== inbox.isAutomationEnabled
      && inbox.isAutomationEnabled) {
      getAutomationMessage(String(serviceProvider.id))
        .then((res) => {
          const automationMessageResponse = res.value.body;
          if (!automationMessageResponse.text) {
            this.setIsAutomationsOnboardingModalOpen(true);
          }
        });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  setIsAutomationsOnboardingModalOpen: any = (isOpen: boolean) => {
    this.setState({
      isAutomationsOnboardingModalOpen: isOpen,
    });
  }

  props: Props;

  resize: (() => void) = () => this.forceUpdate();

  render(): Element<"div"> {
    const {
      serviceProvider, experiment, home,
      performance: {
        contactMetrics: {
          response: contactMetrics,
          isLoading: contactMetricsLoading,
          error: contactMetricsError,
        },
      },
      businessList,
      inbox,
    } = this.props;
    const isAdvertiser = get(serviceProvider, 'isAdvertiser');

    // Northstar
    const isNorthStar = get(serviceProvider, 'isNorthStar', false);

    if ((getCookieByName(isNSDashViewedCookie) === 'false') && isNorthStar) {
      document.cookie = `${encodeURIComponent(isNSDashViewedCookie)}=${encodeURIComponent('true')}; expires=${setExpirationDate(365)}; path=/`;
      window.location.href = `/app/setup/${serviceProvider.id}/about`;
    }
    // End Northstar

    const serviceProviderId = String(serviceProvider.id);
    const {
      metrics: {
        profileCompletion,
        offersTotal,
      } = {},
      accountOwner: {
        name,
        phone,
        email,
      } = {},
    } = serviceProvider;
    const {
      getAllHighlights: {
        error: getAllHighlightsError,
        isLoading: getAllHighlightsIsLoading,
        response: getAllHighlightsResponse,
      },
      updateBusinessHighlights: {
        isLoading: updateBusinessHighlightsIsLoading,
      },
    } = home;

    // Metrics
    const typeMetrics = get(contactMetrics, 'contactTypeMetrics', {});

    const callsMetrics = find(typeMetrics, o => o.contactType === 'Calls');
    const estimatedCallsMetrics = find(typeMetrics, o => o.contactType === 'Estimated Calls');
    const quotesMetrics = find(typeMetrics, o => o.contactType === 'RequestAQuote');
    const dealsMetrics = find(typeMetrics, o => o.contactType === 'DealClaimed');
    const xmlAutomatchMetrics = find(typeMetrics, o => o.contactType === 'XMLAutoMatch');

    const callCount = get(callsMetrics, 'contactCount', 0);
    const estimatedCallCount = get(estimatedCallsMetrics, 'contactCount', 0);
    const quoteRequestsCount = get(quotesMetrics, 'contactCount', 0);
    const claimedDealsCount = get(dealsMetrics, 'contactCount', 0);
    const xmlAutoMatchCount = get(xmlAutomatchMetrics, 'contactCount', 0);
    const totalReviews = get(serviceProvider, 'overallReviewCount', 5);
    const totalQuoteRequestsCount = sum([quoteRequestsCount, xmlAutoMatchCount]);
    const totalCount = sum([
      callCount, estimatedCallCount, totalQuoteRequestsCount, claimedDealsCount]);

    const metricsChartContent = contactMetricsError ? (
      <Alert bsStyle="danger">There was a problem retrieving your performance metrics.  Please contact us at&nbsp;
        <a
          id="service-areas--add-service-area--store-error-link"
          href={urlCatalog.global.businessCenterPhone()}
          style={{ whiteSpace: 'nowrap' }}
        >{urlCatalog.global.businessCenterPhoneText()}
        </a> for assistance.
      </Alert>) : (
        <CustomerContactsChart
          totalToDisplay={contactMetrics ? contactMetrics.aggregatedContactCount : 0}
          callsPercentage={calculateContactsPercentage(
            (callCount + estimatedCallCount),
            totalCount,
          )}
          trackedCallsPercentage={calculateContactsPercentage(
            callCount,
            (callCount + estimatedCallCount),
          )}
          quoteRequestsPercentage={calculateContactsPercentage(totalQuoteRequestsCount, totalCount)}
          claimedDealsPercentage={calculateContactsPercentage(claimedDealsCount, totalCount)}
          strokeWidth={7}
          serviceProviderId={serviceProviderId}
          isAdvertiser={isAdvertiser}
          name={name}
          phoneNumber={phone}
        />);

    // Business Highlights
    const businessHighlights = get(serviceProvider, 'businessHighlights', false);
    const hasBusinessHighlights = !isEmpty(businessHighlights);
    const chunkedHighlights = chunk(businessHighlights, 3);
    const businessHighlightsEnabled = get(experiment, 'experiments.enableBusinessHighlights.enabled', false);

    const handleBusinessHighlightsUpdate = (values) => {
      this.props.updateBusinessHighlights(serviceProviderId, values);
    };

    return (
      <div>
        <div style={{ marginTop: '1em' }}>
          <Grid>
            <Row>
              <Col xs={12} sm={8}>
                <AddedBusinessHandler />
                <h4>New Activity</h4>
                <If condition={serviceProvider}>
                  <NewActivityCard
                    isAdvertiser={isAdvertiser}
                    serviceProviderId={serviceProviderId}
                    dealsCount={get(businessList, `getNewActivity[${serviceProviderId || ''}].unreadDealCount`, 0)}
                    quotesCount={get(businessList, `getNewActivity[${serviceProviderId || ''}].unreadLeadCount`, 0)}
                    messagesCount={inbox.unreadMessageCount}
                    reviewsCount={get(businessList, `getNewActivity[${serviceProviderId || ''}].unreadReviewCount`, 0)}
                    experiment={experiment}
                    name={name}
                    phoneNumber={phone}
                  />
                </If>
                <Loader
                  loading={contactMetricsLoading}
                  element={
                    <Row className="category-row text-center" style={{ marginTop: '1em' }}>
                      <i className="icon icon-load icon-spin icon-3x text-primary" />
                    </Row>}
                >
                  <h4 style={{ marginTop: '1.8em' }}>Past Performance</h4>
                  <SingleCard
                    headerSubtitle="Last 90 Days"
                    ctaText="View Performance Metrics"
                    customWidth={90}
                    metricLabel={metricsChartContent}
                    ctaLink={
                      isAdvertiser
                        ? urlCatalog.office.performance(serviceProviderId)
                        : undefined
                    }
                  />
                </Loader>
              </Col>
              <Col xs={12} sm={4} style={{ marginTop: '2.8em' }}>
                <ClientSuccessManagerCard
                  name={name}
                  phone={phone}
                  email={email}
                />
                <If condition={profileCompletion !== 100}>
                  <ProfileCompletionCard
                    percentComplete={profileCompletion || 25}
                    serviceProviderId={serviceProviderId}
                  />
                </If>
                <If condition={totalReviews < 5}>
                  <SingleCard
                    headerIcon="file-text"
                    headerTitle="Get to 5 Reviews"
                    boldHeaderTitle
                    source="ReviewCompletionCard"
                    ctaText="Request a Review"
                    customWidth={80}
                    ctaLink={urlCatalog.office.requestReviews(serviceProviderId)}
                    metricLabel={
                      <div style={{ textAlign: 'left' }}>
                        Reviews from your loyal customers will help you stand out
                        from other providers in your area.
                        <div style={{ textAlign: 'left', fontWeight: 'bold', margin: '0.7em 0' }}>
                          { `${totalReviews.toString()} / 5 Reviews` }
                        </div>
                        <ProgressBar
                          now={totalReviews * 20}
                          style={{
                            borderRadius: '1em', width: '100%', margin: 'auto', border: `0.5px solid ${stylesColors.backgroundNeutralMedium}`,
                          }}
                        />
                      </div>}
                    buttonProps={{ 'data-track-spid': serviceProviderId }}
                  />
                </If>
                <If condition={offersTotal === 0}>
                  <SingleCard
                    headerIcon="offer-dollars"
                    headerTitle="Deals"
                    ctaText="Learn More"
                    ctaLink={urlCatalog.office.leadBoard(serviceProviderId)}
                    metricLabel="Learn more to get started creating deals."
                    modalContent={<DealsModal ctaText="Learn More" name={name} phoneNumber={phone} />}
                  />
                </If>
                <If condition={businessHighlightsEnabled}>
                  <GenericCard>
                    <Loader
                      loading={getAllHighlightsIsLoading || updateBusinessHighlightsIsLoading}
                      element={
                        <Row className="category-row text-center" style={{ marginTop: '1em' }}>
                          <i className="icon icon-load icon-spin icon-3x text-primary" />
                        </Row>}
                    >
                      <Choose>
                        <When condition={getAllHighlightsError}>
                          <Row>
                            <Col xs={12}>
                              <Alert bsStyle="danger">
                                There was an error processing your Business Highlights,
                                please refresh the page and try again.
                              </Alert>
                            </Col>
                          </Row>
                        </When>
                        <Otherwise>
                          <BusinessHighlightsCard
                            allBusinessHighlights={get(getAllHighlightsResponse, 'businessHighlights', false)}
                            hasBusinessHighlights={hasBusinessHighlights}
                            businessHighlights={businessHighlights}
                            chunkedHighlights={chunkedHighlights}
                            updateBusinessHighlights={handleBusinessHighlightsUpdate}
                          />
                        </Otherwise>
                      </Choose>
                    </Loader>
                  </GenericCard>
                </If>
              </Col>
            </Row>
          </Grid>
        </div>
        <If condition={this.state.isAutomationsOnboardingModalOpen}>
          <AutomationsOnboardingModal
            show={this.state.isAutomationsOnboardingModalOpen}
            spId={serviceProviderId}
            onClose={() => this.setIsAutomationsOnboardingModalOpen(false)}
          />
        </If>
      </div>
    );
  }
}

const serviceProviderDashboardAnalytics = {
  elements: {
    'card-cta-button-leads': itemClicked('SP : Home', 'Manage Leads'),
    'card-cta-button-messages': itemClicked('SP : Home', 'View Messages'),
    'card-cta-button-reviews': itemClicked('SP : Home', 'Click Request Reviews Link', {
      numberOfReviews: '<<attributes.data-track-total-reviews.value>>',
      numberOfRecentReviews: '<<attributes.data-track-recent-reviews.value>>',
      hasAd: true,
      userType: 'Authenticated',
    }),
    'card-cta-button-deals-modal': trackEvent('Modal Fired', 'SP : Home', 'Deals Learn More'),
    'dashboard--deals-modal--phone-button': trackEvent('Phone Number Clicked', 'Deals Learn More', 'Deals Learn More Call'),
    'feedback--start-survey': itemClicked('SP : Home', 'New Advertiser Feedback'),
    'new-activity--view-deals-link': itemClicked('SP : Home', 'New Activity - Deals'),
    'new-activity--view-quotes-link': itemClicked('SP : Home', 'New Activity - RaQ'),
    'new-activity--view-messages-link': itemClicked('SP : Home', 'New Activity - Messages'),
    'new-activity--request-reviews-link': itemClicked('SP : Home', 'Click Request Reviews Link'),
    'new-activity--view-reviews-link': itemClicked('SP : Home', 'New Activity - Reviews'),
    'card-cta-button-deals-modal-link-only': trackEvent('Modal Fired', 'SP : Home', 'Deals Learn More'),
    'card-cta-button-get-to-5-reviews': itemClicked('SP : Home', 'Get to 5 Reviews - Request Reviews'),
    'quick-books-card--sync-button-enabled': itemClicked('SP : Home', 'Sync'),
    'manage-like-a-pro--close-card-button': itemClicked('SP : Home', 'Manage Like a Pro - Close Card'),
    'manage-like-a-pro--learn-more-button': itemClicked('SP : Home', 'Manage Like a Pro - Learn More'),
    'manage-like-a-pro--add-features-button': itemClicked('SP : Home', 'Manage Like a Pro -- Add Features'),
    'Business-Highlights--Edit-Highlights': trackEvent('Modal Fired', 'SP : Home', 'Business Highlights Modal'),
    'Generic-Card--Business-Highlights': trackEvent('Modal Fired', 'SP : Home', 'Business Highlights Modal'),
    'Business-Highlights--Update': itemClicked('Business Highlights Modal', 'Update Business Highlights'),
    'Business-Highlights--Close-Modal': itemClicked('Business Highlights Modal', 'Not Now'),
    'profile-incomplete--finish-profile-button-container': itemClicked('SP : Home', 'Finish profile'),
    'help-call-button': itemClicked('SP : Home', 'Help card call'),
    'help-link-button': itemClicked('SP : Home', 'Visit help center'),
    'learn-more-link': itemClicked('SP : Home', 'Recent activity learn more'),
    'views--reviews-card-container': itemClicked('SP : Home', 'Reviews activity card'),
    'views--leads-card-container': itemClicked('SP : Home', 'Leads activity card'),
    'views--hours-card-container': itemClicked('SP : Home', 'Hours activity card'),
    'views--address-card-container': itemClicked('SP : Home', 'Address activity card'),
    'views--services-card-container': itemClicked('SP : Home', 'Services offered activity card'),
    'premium-upsell-card-container': itemClicked('SP : Home', 'Premium upgrade learn more'),
    'mini-business-card-container': itemClicked('SP : Home', 'Business card enter profile'),
    'metrics-card-item--directory-views': itemClicked('SP : Home', 'Directory views metrics card'),
    'metrics-card-item--profile-visits': itemClicked('SP : Home', 'Profile visits metrics card'),
    'metrics-card-item--leads': itemClicked('SP : Home', 'Leads metrics card'),
    'learn-more-about-premium--leads-calculator': itemClicked('SP : Home', 'Lead calculator cta'),
    'how-we-estimate-button--leads-calculator': itemClicked('SP : Home', 'how we estimate your leads'),
  },
};

export default (compose(
  analytics({
    ruleset: serviceProviderDashboardAnalytics,
  }),
  connect(state => ({
    experiment: state.experimentV2,
    performance: state.performance,
    customerAttributesState: state.customerAttributesState,
    advertise: state.advertise,
    home: state.home,
    businessList: state.businessList,
    serviceProviderProfile: state.serviceProviderProfile,
    images: state.images,
    inbox: state.inbox,
  }), {
    ...serviceProviderActions,
    ...performanceActions,
    ...advertiseActions,
    ...analyticsActions,
    ...homeActions,
    ...profileActions,
    ...categoryGroupActions,
    ...inboxActions,
  }),
  pure,
)(ServiceProviderDashboardComponent): any);



// WEBPACK FOOTER //
// ./src/modules/home/browser/components/ServiceProviderDashboard.js